import axios, { isAxiosError } from 'axios'
import { User, UserSchema } from '../model/user'
import { useQuery, QueryFunctionContext } from '@tanstack/react-query'
import Command from '../helpers/Command'
import { queryClient } from '../queryClient'
import posthog from 'posthog-js'
import { z } from 'zod'

const LEGACY_PLATFORM_TOKEN_KEY = 'kvk-vb-token'
const AUTH_QUERY_KEY = 'auth'

export type LoginCredentials = {
  email: string
  password: string
}

type LoginSuccess = {
  success: true
  user: User
}

type LoginFailure = {
  success: false
  reason: string
}

export type WistorAuth = {
  isAuthenticated: false
  status: 'unauthenticated'
  error?: Error
} | {
  isAuthenticated: false
  status: 'authenticating'
} | {
  isAuthenticated: true
  status: 'unauthorized'
  errorMessage: string
  visibleSingleLogin: boolean
  visibleSingleLogout: boolean
  isSingleLogin: boolean
  logout: () => Promise<void>
} | {
  isAuthenticated: true
  status: 'authorized'
  user: User
  isSingleLogin: boolean
  logout: () => Promise<void>
}

/** Hook that should provide all things related to auth-api.  */
export function useWistorAuth(): WistorAuth {
  const legacyToken = getLegacyToken()

  const platformRightsQuery = useQuery({
    queryKey: [AUTH_QUERY_KEY, 'platform-rights', legacyToken],
    queryFn: ({queryKey: [,,token]}: QueryFunctionContext<[any, any, string | null | undefined]>) => {
      return fetchPlatformUserRights(token)
    },
    staleTime: Infinity,
    refetchOnMount: false,
    retryOnMount: false,
    retry: (failureCount: number, _error: Error) => {
      console.error(_error)
      return failureCount < 2
    },
  })

  const logout = async () => {
    window.location.href = '/rest/oidc/logout'
  }

  if (platformRightsQuery.status === 'pending') return {
    isAuthenticated: false,
    status: 'authenticating',
  }

  if (platformRightsQuery.status === 'success' && !platformRightsQuery.data.success) {
    if ('isAuthenticated' in platformRightsQuery.data && !platformRightsQuery.data.isAuthenticated) {
      return {
        status: 'unauthenticated',
        isAuthenticated: false,
      }
    }

    return {
      status: 'unauthorized',
      isAuthenticated: true,
      isSingleLogin: platformRightsQuery.data.loginMethod === 'single-login',
      errorMessage: platformRightsQuery.data.errorForEndUser,
      visibleSingleLogin: false,
      visibleSingleLogout: platformRightsQuery.data.visibleSingleLogout,
      logout,
    }
  }

  if (platformRightsQuery.status === 'success' && platformRightsQuery.data.success) {
    const {id: userId, project: _, ...userProps} = platformRightsQuery.data
    posthog.identify(userId, userProps)

    return {
      status: 'authorized',
      isAuthenticated: true,
      isSingleLogin: platformRightsQuery.data.loginMethod === 'single-login',
      user: platformRightsQuery.data,
      logout,
    }
  }

  return {
    status: 'unauthenticated',
    isAuthenticated: false,
  }
}

export function invalidateWistorAuth() {
  return queryClient.invalidateQueries({ queryKey: [ AUTH_QUERY_KEY ]})
}

const LoginMethodSchema = z.union([
  z.literal('single-login'),
  z.literal('legacy'),
])

const InvalidUserSchema = z.object({
  loginMethod: z.union([z.literal('legacy'), z.literal('single-login')]),
  email: z.optional(z.string()),
  errorForEndUser: z.string(),
  loginAgainRecommendation: z.optional(z.literal(true)),
})


async function fetchPlatformUserRights(token: string | null | undefined) {
  try {

    const response = await axios.get('/api/me', {
      headers: {Authorization: token ? `Bearer ${token}` : undefined}
    })

    if (!response.data.me) {
      // noinspection ExceptionCaughtLocallyJS
      throw new Error(ERR_INVALID_RESPONSE_WITH_SUGGESTION)
    }

    const user = UserSchema.parse(response.data.me)
    const loginMethod = LoginMethodSchema.parse(response.data.loginMethod)
    return {...user, loginMethod, success: true} as const

  } catch (error) {
    if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      if (error.response.data === "Unauthorized") { // default koa.js response for empty 401
        return { isAuthenticated: false } as const
      }

      const feedback = InvalidUserSchema.parse(error.response.data)

      if ('loginAgainRecommendation' in feedback && feedback.loginAgainRecommendation) {
        if (feedback.loginMethod === 'legacy') {
          logoutPlatformLegacy()
        } else if (feedback.loginMethod === 'single-login') {
          // we cannot do anything with http-only cookies in front-end, so cookies should be destroyed by the back-end
        } else {
          throw new Error('An unexpected error was encountered')
        }
      }

      return {
        success: false,
        loginMethod: feedback.loginMethod,
        errorForEndUser: feedback.errorForEndUser,
        visibleSingleLogout: feedback.loginMethod === 'single-login'
      } as const
    }

    throw error
  }
}

export function logoutAppLegacy(repoURL: string): Promise<void> {
  return new Promise(resolve => {
    Command.sendCommand({ props: { repoURL} }, 'logout', null, resolve)
  })
}

export async function loginPlatformLegacy(credentials: LoginCredentials): Promise<LoginSuccess | LoginFailure> {
  try {
    const response = await axios.post('/api/login', credentials)
    const token = response.data.token as string
    setLegacyToken(token)

    const user = UserSchema.parse(response.data.me)
    return { success: true, user }
  } catch (error) {
    logoutPlatformLegacy()

    if (!isAxiosError(error)) throw error

    if (typeof error.response?.data !== 'string') {
      console.error(error)
      return { success: false, reason: 'Request failure' }
    }

    return { success: false, reason: error.response?.data }
  }
}

export function logoutPlatformLegacy() {
  localStorage.removeItem(LEGACY_PLATFORM_TOKEN_KEY)
  void invalidateWistorAuth()
}

function getLegacyToken(): string | null {
  return localStorage.getItem(LEGACY_PLATFORM_TOKEN_KEY)
}

function setLegacyToken(token: string) {
  localStorage.setItem(LEGACY_PLATFORM_TOKEN_KEY, token)
  void invalidateWistorAuth()
}

const ERR_INVALID_RESPONSE_WITH_SUGGESTION =
  'Invalid response! When developing use `yarn dev`.'

/** This only provides a bearer token when using legacy platform login */
export function getAuthHeaders(): Record<string, string> {
  const legacyToken = getLegacyToken()
  if (!legacyToken) return {}
  else return { Authorization: `Bearer ${legacyToken}`, }
}
